import { Link } from 'react-router-dom';
import { ArrowRight, KeyRound, ShieldCheck, Users, type LucideIcon } from 'lucide-react';
import { Blueprint, buttonVariants } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { cn } from '@/lib/utils';

interface HubCard {
  Icon: LucideIcon;
  title: string;
  body: string;
  cta: string;
  to: string;
  testId: string;
}

const CARDS: readonly HubCard[] = [
  {
    Icon: KeyRound,
    title: ptBR.config.cardApiKeysTitle,
    body: ptBR.config.cardApiKeysBody,
    cta: ptBR.config.cardApiKeysCta,
    to: '/configuracoes/api-keys',
    testId: 'config-card-api-keys',
  },
  {
    Icon: Users,
    title: ptBR.config.cardMembersTitle,
    body: ptBR.config.cardMembersBody,
    cta: ptBR.config.cardMembersCta,
    to: '/configuracoes/usuarios',
    testId: 'config-card-members',
  },
  {
    Icon: ShieldCheck,
    title: ptBR.config.cardContaTitle,
    body: ptBR.config.cardContaBody,
    cta: ptBR.config.cardContaCta,
    to: '/configuracoes/escritorio',
    testId: 'config-card-conta',
  },
];

function ConfigCard({ card }: { card: HubCard }) {
  return (
    <Blueprint
      as="article"
      className="flex h-full flex-col gap-sc-3 p-sc-6"
      data-testid={card.testId}
    >
      <header className="flex items-center justify-between">
        {/* Ícone em desenho de linha, stroke 1.5 — sem a plaqueta sólida. */}
        <span className="grid h-9 w-9 place-items-center border border-border text-accent-700">
          <card.Icon className="h-5 w-5" aria-hidden="true" strokeWidth={1.5} />
        </span>
      </header>
      <h2 className="text-heading-sm text-fg">{card.title}</h2>
      <p className="flex-1 text-body-sm text-fg-muted">{card.body}</p>
      <Link
        to={card.to}
        className={cn(
          buttonVariants({ variant: 'secondary', size: 'sm' }),
          'gap-1 self-start no-underline',
        )}
        data-testid={`${card.testId}-cta`}
      >
        {card.cta}
        <ArrowRight className="h-4 w-4" aria-hidden="true" strokeWidth={1.5} />
      </Link>
    </Blueprint>
  );
}

export function ConfigPage() {
  return (
    <section aria-labelledby="config-title" data-testid="config-page" className="mx-auto max-w-5xl">
      <p className="mb-1.5 font-titulo text-rotulo uppercase text-fg-dim">{ptBR.config.eyebrow}</p>
      <h1 id="config-title" className="text-display-md text-fg">
        {ptBR.config.pageTitle}
      </h1>
      <p className="mt-2 max-w-prose text-body-md text-fg-muted">{ptBR.config.intro}</p>

      <div
        className="mt-sc-8 grid grid-cols-1 gap-sc-4 sm:grid-cols-2 lg:grid-cols-3"
        data-testid="config-cards"
      >
        {CARDS.map((card) => (
          <ConfigCard key={card.testId} card={card} />
        ))}
      </div>
    </section>
  );
}
